import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";

import useLocation from "../hooks/useLocation";
import type { CentraAddress } from "../lib/Selection/formatters";
import useSelection from "../lib/Selection/useSelection";
import { useConfig } from "../lib/useConfig";
import GiftCardItem from "./GiftCardItem";
import StateDropdown from "./StateDropdown";

const ErrorMessageComp = ({ name }: { name: string }) => {
  return (
    <span className="error">
      <ErrorMessage name={name} />
    </span>
  );
};

const AddressForm = ({
  callback,
}: {
  callback: (values: CentraAddress) => void;
}) => {
  const { currentCountry } = useLocation();

  const { config } = useConfig();

  const { data } = useSelection();

  const address = data?.selection.address;

  const initialName = address?.firstName || "";
  const initialLastName = address?.lastName || "";
  const initialEmail = address?.email || "";
  const initialPhoneNumber = address?.phoneNumber || "";
  const initialAddress1 = address?.address1 || "";
  const initialAddress2 = address?.address2 || "";

  const intialZipCode = address?.zipCode || "";
  const initialCity = address?.city || "";
  const initialState = address?.state || "";

  return (
    <Formik
      initialValues={{
        country: currentCountry?.country || config?.centra?.country,
        firstName: initialName,
        lastName: initialLastName,
        email: initialEmail,
        phoneNumber: initialPhoneNumber,
        address1: initialAddress1,
        address2: initialAddress2,
        zipCode: intialZipCode,
        city: initialCity,
        state: initialState,
      }}
      validationSchema={Yup.object({
        country: Yup.string().required("Required"),
        firstName: Yup.string()
          .max(15, "Must be 15 characters or less")
          .required("Required"),
        lastName: Yup.string()
          .max(20, "Must be 20 characters or less")
          .required("Required"),
        email: Yup.string().email("Invalid email address").required("Required"),
        phoneNumber: Yup.string().required("Required"),
        address1: Yup.string().required("Required"),
        address2: Yup.string().optional(),
        zipCode: Yup.string().required("Required"),
        city: Yup.string().required("Required"),
        state: !!currentCountry?.states
          ? Yup.string().required("Required")
          : Yup.string().optional(),
      })}
      onSubmit={(values, { setSubmitting, validateForm }) => {
        validateForm();
        callback({
          ...values,
          country: currentCountry?.country || config?.centra.country!,
        });
        setSubmitting(false);
      }}
    >
      <>
        <GiftCardItem />
        <Form className="form giftcard-form">
          <h3 className="form-title">{config?.addressFields.title}</h3>
          <div className="multi">
            <div>
              <label htmlFor="firstName">
                {config?.addressFields.firstName.label}
              </label>
              <Field name="firstName" type="text" />
              <ErrorMessageComp name="firstName" />
            </div>
            <div>
              <label htmlFor="lastName">
                {config?.addressFields.lastName.label}
              </label>
              <Field name="lastName" type="text" />
              <ErrorMessageComp name="lastName" />
            </div>
          </div>
          <div>
            <label htmlFor="email">{config?.addressFields.email.label}</label>
            <Field name="email" type="text" />
            <ErrorMessageComp name="email" />
          </div>
          <div>
            <label htmlFor="phoneNumber">
              {config?.addressFields.phoneNumber.label}
            </label>
            <Field name="phoneNumber" type="text" />
            <ErrorMessageComp name="phoneNumber" />
          </div>
          <div>
            <label htmlFor="address1">
              {config?.addressFields.address1.label}
            </label>
            <Field name="address1" type="text" />
            <ErrorMessageComp name="address1" />
          </div>
          <div>
            <label htmlFor="address2">
              {config?.addressFields.address2.label}
            </label>
            <Field name="address2" type="text" />
            <ErrorMessageComp name="address2" />
          </div>

          <div className="multi">
            <div>
              <label htmlFor="zipCode">
                {config?.addressFields.zipCode.label}
              </label>
              <Field name="zipCode" type="text" />
              <ErrorMessageComp name="zipCode" />
            </div>
            <div>
              <label htmlFor="city">{config?.addressFields.city.label}</label>
              <Field name="city" type="text" />
              <ErrorMessageComp name="city" />

              <StateDropdown name="state" label="State" />
              <ErrorMessageComp name="state" />
            </div>
          </div>

          <div>
            <label htmlFor="country">
              {config?.addressFields.country.label}
            </label>
            {/* <CountryDropdown name="country" label="Country" /> */}
            <p>{currentCountry?.name}</p>

            <ErrorMessageComp name="country" />
          </div>
          <button type="submit">{config?.addressFields.button}</button>
        </Form>
      </>
    </Formik>
  );
};

export default AddressForm;
